import { submissionErrorMessages } from '@react/gwizzy/helpers/validateFormFields';
import { useQuery } from '@react/services/api';

type SupportedValidationFields = 'phone' | 'email';

type ValidationIO = {
  [key in SupportedValidationFields]?: string;
};

export default function useValidateFormSubmission({
  onSubmit,
}: {
  onSubmit: (values: Record<string, any>) => Promise<void>;
}) {
  const applyFormValidation = useQuery('applyFormValidation');

  const validateFormSubmission = async ({
    values,
  }: {
    values: ValidationIO;
  }) => {
    if (values && Object.keys(values).length === 0) return null;
    const errors: ValidationIO = {};

    try {
      const {
        body,
      }: {
        body: Record<SupportedValidationFields, any>;
      } = await applyFormValidation(values);

      if (body) {
        Object.entries(body).forEach(([fieldName, val]) => {
          const { isValid, reason } = val;
          const errorMessage =
            submissionErrorMessages[fieldName as SupportedValidationFields] ||
            reason ||
            'Please enter a valid information';

          if (!isValid) {
            errors[fieldName as SupportedValidationFields] = errorMessage;
          }
        });
      }
    } catch (e) {
      console.error(e);
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const handleSubmitWithValidation = async (values: Record<string, any>) => {
    const validateFields: ValidationIO = {};

    if (values?.email) {
      validateFields.email = values.email;
    }

    if (values?.phone) {
      validateFields.phone = values.phone;
    }

    const errors = await validateFormSubmission({
      values: validateFields,
    });

    if (errors) {
      return errors;
    }

    await onSubmit(values);
  };

  return handleSubmitWithValidation;
}
